import { CardGiftcard } from '@mui/icons-material';
import { Box, Button, Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import useAwardComponentStyles from './useAwardComponentStyles';
import { Award, User, Notification, NotificationType } from '../../../../types';
import useGlobalStyles from '../../../useGlobalStyles';
import useLayoutStyles from '../../../layout/useLayoutStyles';
import { addAward } from '../../../../services/userService';
import useAchievementTracking from '../../../../hooks/useAchievementTracking';
import { currentUserSelector } from '../../../../redux/slices/userSlice';
import { useAppSelector } from '../../../../redux/hooks';
import addNotification from '../../../../services/notificationService';

interface AwardComponentProps {
  awardReceiver: User | null;
  disabled: boolean;
}

export const AWARDS: { key: string; value: Award }[] = [
  {
    key: 'helpful_penguin',
    value: {
      name: 'Helpful Penguin',
      description: 'For being a helpful penguin!',
      icon: 'helpful-penguin.png',
      points: 1,
    },
  },
  {
    key: 'goated',
    value: {
      name: 'U R Goated',
      description: 'For being the absolute GOAT!',
      icon: 'goated.png',
      points: 2,
    },
  },
  {
    key: 'beast_mode',
    value: {
      name: 'Beast Mode',
      description: 'For being the BEAST of the best!',
      icon: 'beastmode.png',
      points: 3,
    },
  },
  {
    key: 'cookie_jar',
    value: {
      name: 'Cookie Jar',
      description: 'NOM NOM!',
      icon: 'cookie-jar.png',
      points: 1,
    },
  },
  {
    key: 'eldery',
    value: {
      name: 'Elder',
      description: 'Old people give the wisest advice',
      icon: 'eldery.png',
      points: 2,
    },
  },
  {
    key: 'speaker',
    value: {
      name: 'Speaker',
      description: 'Thanks for being the voice of the people!',
      icon: 'speaker.png',
      points: 3,
    },
  },
  {
    key: 'teletubby',
    value: {
      name: 'Teletubby',
      description: 'Childlike wonder!',
      icon: 'teletubby.png',
      points: 1,
    },
  },
  {
    key: 'triceratops',
    value: {
      name: 'Triceratops',
      description: 'Thanks for TRI-ing your best',
      icon: 'triceratops.png',
      points: 1,
    },
  },
  {
    key: 'wizard',
    value: {
      name: 'Wizard',
      description: 'UR a wizard, Harry!',
      icon: 'wizard.png',
      points: 3,
    },
  },
  {
    key: 'bone',
    value: {
      name: 'Bone',
      description: 'For being a doggone good friend!',
      icon: 'bone.png',
      points: 1,
    },
  },
];

const AwardComponent = ({ awardReceiver, disabled }: AwardComponentProps) => {
  const { classes } = useAwardComponentStyles();
  const { classes: layoutClasses } = useLayoutStyles();
  const { classes: globalClasses } = useGlobalStyles();

  const currentUser = useAppSelector(currentUserSelector);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [message, setMessage] = useState('');
  const { addAchievementIfEarned } = useAchievementTracking({
    achievementType: 'award',
  });
  const [sentAwards, setSentAwards] = useState<string[]>([]);

  useEffect(() => {
    setMessage('');
    setSentAwards([]); // Reset sent awards when the modal is closed
  }, [open]);

  const onClickAward = async (award_key: string) => {
    try {
      if (!currentUser) {
        return;
      }
      const awardKeyValue = AWARDS.find(award => award.key === award_key);
      if (!awardKeyValue) {
        return;
      }
      setSentAwards([...sentAwards, award_key]);
      const awardToSend = awardKeyValue.value;
      const receiverId = awardReceiver?._id || '';
      await addAward(receiverId, award_key);
      if (awardReceiver) {
        await addAchievementIfEarned(
          currentUser as User,
          awardReceiver,
          achievement => achievement.goal - achievement.progress === 1,
        );
        // notify the user that they have been awarded
        const notif: Notification = {
          message: `You were awarded: ${awardToSend.name} by {sender}`,
          sender: currentUser,
          receiver: awardReceiver,
          notificationType: NotificationType.Award,
          notificationDateTime: new Date(),
          path: `/profile/{sender}`,
        };
        await addNotification(notif);
        setMessage(
          `Successfully awarded ${awardToSend.name} to ${awardReceiver?.username || 'Deleted User'}`,
        );
      }
    } catch (error) {
      // error
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div>
      {disabled ? (
        <Button variant='outlined' disabled>
          <CardGiftcard />
        </Button>
      ) : (
        <Button variant='outlined' onClick={handleOpen}>
          <CardGiftcard />
        </Button>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'>
        <Box className={globalClasses.modal}>
          <div className={globalClasses.modalHeader}>
            <div className={layoutClasses.boldTitle}>
              Award {awardReceiver?.username || 'Deleted User'}
            </div>
            <CloseIcon className={globalClasses.closeButton} onClick={handleClose} />
          </div>
          <Box className={classes.awardsContainer}>
            {AWARDS.map((award, idx) => (
              <Box className={classes.awardContainer} key={idx}>
                <div className={classes.awardInfo}>
                  <img
                    className={classes.awardImage}
                    src={`${process.env.PUBLIC_URL}/assets/awards/${award.value.icon}`}
                    alt={award.value.name}
                  />
                  <div className={classes.awardName}>{award.value.name}</div>
                  <div className={classes.awardDescription}>{award.value.description}</div>
                </div>
                <Button
                  disabled={sentAwards.includes(award.key)}
                  className={classes.awardButton}
                  onClick={() => onClickAward(award.key)}>
                  Give Award
                </Button>
              </Box>
            ))}
          </Box>
          <div className={classes.messageContainer}>{message}</div>
        </Box>
      </Modal>
    </div>
  );
};

export default AwardComponent;
